import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-calendar-check-line',
    color: 'text-[#f97316]',
    bg: 'bg-[#f97316]/10',
    border: 'border-[#f97316]/30',
    glow: 'shadow-[0_0_30px_rgba(249,115,22,0.15)]',
    title: 'Book the Job',
    subtitle: 'Customer or front desk creates a job card in seconds',
    description: 'Customers book online or walk in — your team creates a digital job card with vehicle details, service type, and customer info. Fully linked to VIN history.',
    mockup: (
      <div className="bg-[#0a1628] rounded-xl p-4 border border-[#f97316]/20">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 flex items-center justify-center bg-[#f97316]/15 rounded-md">
            <i className="ri-file-add-line text-[#f97316] text-xs"></i>
          </div>
          <span className="text-white text-xs font-semibold">New Job Card</span>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs w-20">Vehicle</span>
            <span className="text-white text-xs bg-white/5 px-2 py-1 rounded flex-1">Toyota Hilux — GP 45 ABC</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs w-20">Service</span>
            <span className="text-white text-xs bg-white/5 px-2 py-1 rounded flex-1">Full Service + Brake Pads</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs w-20">Customer</span>
            <span className="text-white text-xs bg-white/5 px-2 py-1 rounded flex-1">Thabo Mokoena</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs w-20">Assigned</span>
            <span className="text-[#f97316] text-xs bg-[#f97316]/10 px-2 py-1 rounded flex-1">Sipho M. — Bay 3</span>
          </div>
        </div>
        <button className="w-full mt-3 py-2 bg-[#f97316] text-white text-xs font-semibold rounded-lg">
          Create Job Card
        </button>
      </div>
    ),
  },
  {
    number: '02',
    icon: 'ri-radar-line',
    color: 'text-[#3b82f6]',
    bg: 'bg-[#3b82f6]/10',
    border: 'border-[#3b82f6]/30',
    glow: 'shadow-[0_0_30px_rgba(59,130,246,0.15)]',
    title: 'Track Progress',
    subtitle: 'Real-time updates for your team and customers',
    description: 'Technicians update job status from their mobile app. Customers receive automatic SMS/WhatsApp notifications. Managers see every bay\'s status live on the dashboard.',
    mockup: (
      <div className="bg-[#0a1628] rounded-xl p-4 border border-[#3b82f6]/20">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-xs font-semibold">Live Job Tracker</span>
          <span className="flex items-center gap-1 text-green-400 text-xs">
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
            Live
          </span>
        </div>
        <div className="space-y-2.5">
          {[
            { vehicle: 'Hilux GP 45 ABC', stage: 'In Progress', pct: 70, color: 'bg-[#f97316]' },
            { vehicle: 'VW Polo CL 12 XYZ', stage: 'Awaiting Parts', pct: 30, color: 'bg-yellow-400' },
            { vehicle: 'BMW CA 99 KLM', stage: 'Ready', pct: 100, color: 'bg-green-400' },
          ].map((j) => (
            <div key={j.vehicle}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-300 text-xs">{j.vehicle}</span>
                <span className={`text-xs font-medium ${j.pct === 100 ? 'text-green-400' : j.pct >= 60 ? 'text-[#f97316]' : 'text-yellow-400'}`}>{j.stage}</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${j.color} transition-all duration-700`} style={{ width: `${j.pct}%` }}></div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 p-2 bg-[#3b82f6]/10 rounded-lg flex items-center gap-2">
          <i className="ri-notification-3-line text-[#3b82f6] text-sm"></i>
          <span className="text-gray-300 text-xs">SMS sent to customer — BMW ready</span>
        </div>
      </div>
    ),
  },
  {
    number: '03',
    icon: 'ri-receipt-line',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
    border: 'border-green-400/30',
    glow: 'shadow-[0_0_30px_rgba(74,222,128,0.12)]',
    title: 'Invoice & Pay',
    subtitle: 'Auto-generated VAT invoices and instant payment',
    description: 'Once the job is done, PRODIFY auto-generates a VAT-compliant invoice with all parts and labour. Send it via email/WhatsApp and accept payment — all in one click.',
    mockup: (
      <div className="bg-[#0a1628] rounded-xl p-4 border border-green-400/20">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-xs font-semibold">Tax Invoice</span>
          <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full font-medium">VAT Compliant</span>
        </div>
        <div className="space-y-1.5 mb-3">
          {[
            { item: 'Labour — Full Service (2h)', price: 'R 1,200' },
            { item: 'Brake Pads — Front Set', price: 'R 850' },
            { item: 'Engine Oil 5W-30 (5L)', price: 'R 480' },
            { item: 'Oil Filter', price: 'R 95' },
          ].map((l) => (
            <div key={l.item} className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">{l.item}</span>
              <span className="text-white text-xs font-medium">{l.price}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 pt-2 flex items-center justify-between mb-3">
          <span className="text-gray-400 text-xs">VAT (15%)</span>
          <span className="text-gray-300 text-xs">R 393.75</span>
        </div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-sm font-bold">Total</span>
          <span className="text-green-400 text-sm font-bold">R 3,018.75</span>
        </div>
        <div className="flex gap-2">
          <button className="flex-1 py-1.5 bg-green-400/10 text-green-400 text-xs font-semibold rounded-lg border border-green-400/20">
            <i className="ri-whatsapp-line mr-1"></i>Send
          </button>
          <button className="flex-1 py-1.5 bg-green-400 text-[#0a1628] text-xs font-semibold rounded-lg">
            Paid ✓
          </button>
        </div>
      </div>
    ),
  },
];

const HowItWorks = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#0a1628] py-24 px-6" id="how-it-works" ref={ref}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-route-line text-[#f97316] text-sm"></i>
            <span className="text-gray-300 text-xs font-semibold tracking-widest uppercase">Simple 3-Step Flow</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Up and Running in
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#f97316] to-[#3b82f6]">
              Minutes, Not Months
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            No complex setup. No IT team needed. Your workshop can go live with PRODIFY today and start managing jobs professionally from day one.
          </p>
        </div>

        {/* Steps */}
        <div className="grid lg:grid-cols-3 gap-6 relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-16 left-1/3 right-1/3 h-px bg-gradient-to-r from-[#f97316]/40 via-[#3b82f6]/40 to-green-400/40 z-0"></div>
          <div className="hidden lg:block absolute top-16 left-0 right-2/3 h-px bg-[#f97316]/20 z-0"></div>

          {steps.map((step, i) => (
            <div
              key={i}
              className={`relative bg-[#0f2040] border ${step.border} rounded-2xl p-6 flex flex-col gap-5 transition-all duration-700 ${step.glow} ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 180}ms` }}
            >
              {/* Step number badge */}
              <div className="flex items-center justify-between">
                <div className={`w-12 h-12 flex items-center justify-center ${step.bg} rounded-xl`}>
                  <i className={`${step.icon} ${step.color} text-2xl`}></i>
                </div>
                <span className="text-5xl font-black text-white/5 select-none" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {step.number}
                </span>
              </div>

              {/* Text */}
              <div>
                <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {step.title}
                </h3>
                <p className={`text-xs font-semibold ${step.color} mb-3`}>{step.subtitle}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
              </div>

              {/* Mockup */}
              <div className="mt-auto">
                {step.mockup}
              </div>

              {/* Arrow connector (between cards, desktop) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-[#0f2040] border border-white/10 rounded-full">
                  <i className="ri-arrow-right-s-line text-gray-400 text-lg"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-14 text-center">
          <p className="text-gray-400 text-sm mb-5">
            Trusted by workshops and parts suppliers across Johannesburg, Cape Town, Durban &amp; beyond.
          </p>
          <a
            href="#demo"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#f97316] hover:bg-[#ea6c0a] text-white font-semibold rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-play-circle-line"></i>
            See It In Action — Book a Demo
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
